/**
 * wizardStepsConfig.js
 * הגדרת שלבי הוויזרד + תיאורי מבוא לכל סוג רכיב
 *
 * כל ערך בקונפיג מכיל:
 *  - intro: { title, description, note, whenToUse }
 *  - steps: מערך של { id, label, componentName, skippable? }
 *
 * הקומפוננטה WidgetSetupWizard משתמשת ב-componentName כדי לבחור את שלב ה-UI המתאים.
 * שלב 'intro' ושלב 'template' הם תמיד ראשון ואחרון (מטופלים ישירות בתוך הוויזרד).
 */

export const WIZARD_CONFIG = {

  // ─────────────────────────────────────────────
  InyaneiHaYomWidget: {
    intro: {
      title: 'ענייני היום',
      emoji: '📖',
      description:
        'מציג את כל ענייני ההלכה והתפילה הרלוונטיים ליום — תחנון, הלל, תוספות בתפילה, גשם וטל, סליחות, מנהגים עונתיים, קריאת תורה, ועוד. כל קטגוריה ניתנת להפעלה/כיבוי בנפרד.\n\n' +
        'הרכיב מציג רק דברים בולטים וחריגים: אין תחנון (עם סיבה), הלל, ימי מעבר, עירוב תבשילין, יזכור — ולא מידע שגרתי. תומך בהבדלי עדות (אשכנז/ספרד/מזרחי) ובהצצה קדימה עד שבוע.',
      note: 'מתעדכן אוטומטית בשקיעה ובחצות. ניתן להפעיל הצצה קדימה שמציגה רק שינויים צפויים מול היום.',
      whenToUse: 'רכיב מרכזי לכל בית כנסת — מרכז את כל המידע ההלכתי-תפילתי במקום אחד.',
    },
    steps: [
      { id: 'intro',    label: 'מבוא',          componentName: 'IntroStep' },
      { id: 'settings', label: 'הגדרות',         componentName: 'InyaneiSettingsStep' },
      { id: 'template', label: 'תבנית תצוגה',   componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  ZmanimWidget: {
    intro: {
      title: 'ציר הזמן היומי',
      emoji: '🕰️',
      description:
        'מציג את הזמנים ההלכתיים לאורך היום — מעלות השחר ועד צאת הכוכבים — על ציר זמן חי המתעדכן בזמן אמת לפי מיקום בית הכנסת.\n\n' +
        'היתרון הייחודי: הציר מאחד את כל זמני הקהילה במקום אחד — תפילות יום חול, זמני שבת, וזמני מועד מכל הרכיבים האחרים מוזרמים אוטומטית לציר ומוצגים ביחס יחסי לזמנים ההלכתיים ולשעה הנוכחית. כך ניתן לראות בבת אחת: איפה אנחנו עכשיו, מה עבר, ומה עומד להגיע — בתצוגה שמייצגת את היחסים הנכונים בזמן.\n\n' +
        'ניתן לבחור בדיוק אילו זמנים הלכתיים יוצגו על הציר — מרשימה מלאה של כל הזמנים האפשריים הנשלפים מה-API.',
      note: 'זמני תפילות יום חול, שבת ומועד מוזרמים אוטומטית מרכיביהם — אין צורך להגדיר אותם כאן. ניתן לשלוט בנפרד על אילו זמנים הלכתיים יוצגו על הציר דרך שלב "זמנים על הציר" בהגדרות. הרכיב מתעדכן מדי יום בשעה 02:05, ובכל יום חמישי ב-12:30 טוען מראש את זמני השבת הקרובה.',
      whenToUse: 'רכיב עוגן שכדאי שיהיה בכל לוח — הוא הבסיס שממנו שאר רכיבי הזמן יונקים את חישוביהם ומציגים עצמם.',
    },
    steps: [
      { id: 'intro',            label: 'מבוא',              componentName: 'IntroStep' },
      { id: 'zmanimVisibility', label: 'זמנים על הציר',     componentName: 'ZmanimVisibilityStep', skippable: true },
      { id: 'customTimes',      label: 'זמנים נוספים',      componentName: 'CustomTimesStep', skippable: true },
      { id: 'inyanei',          label: 'ענייני היום',        componentName: 'ZmanimInyaneiStep', skippable: true },
      { id: 'template',         label: 'תבנית תצוגה',       componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  ShabbatWidget: {
    intro: {
      title: 'זמני שבת',
      emoji: '🕯️',
      description:
        'מציג זמני כניסה ויציאת שבת — גם ליום שישי וגם ליום שבת — בתצוגה אחת. הנתונים מתרעננים אוטומטית מדי שבוע בחצות שבת.',
      note: 'הרכיב מוצג כל ימות השבוע, לא רק בסוף שבוע. ניתן להוסיף דרשת ליל שבת לכל פרשה — תוצג אוטומטית בשבת המתאימה.',
      whenToUse: 'רכיב קבוע ושוטף שמשמש את הקהל לאורך כל השבוע.',
    },
    steps: [
      { id: 'intro',       label: 'מבוא',            componentName: 'IntroStep' },
      { id: 'calc',        label: 'כניסה ויציאה',    componentName: 'ShabbatCalcStep' },
      { id: 'prayers',     label: 'תפילות שבת',      componentName: 'PrayerListStep',  skippable: true, props: { mode: 'shabbat' } },
      { id: 'drashot',     label: 'דרשת ליל שבת',     componentName: 'DrashotStep',     skippable: true },
      { id: 'leyning',     label: 'קריאת תורה',       componentName: 'LeyningSettingsStep', skippable: true },
      { id: 'template',    label: 'תבנית תצוגה',     componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  MoadWidget: {
    intro: {
      title: 'זמני מועד',
      emoji: '📅',
      description:
        'מציג זמני כניסה ויציאה לימים שהמשתמש מגדיר — רכיב גמיש לחלוטין: אפשר לשים בו ימי ראש השנה, ימי פסח, ימי סוכות, שבועות, או כל מועד אחר לפי בחירה.',
      note: 'כל מועד ראוי למופע נפרד של הרכיב — רכיב אחד לפסח, רכיב נפרד לסוכות וכו׳. הרכיב מוצג כל עוד הוא נמצא בלוח — כשמסתיים המועד, יש להסיר אותו ידנית. הזמנים מחושבים אוטומטית: שקיעה וצאת כוכבים נשאבים מהיום שלפני כניסת המועד (ערב החג).',
      whenToUse: 'לתקופות חגים — להוסיף רכיב לפני החג ולהסיר לאחריו.',
    },
    steps: [
      { id: 'intro',    label: 'מבוא',             componentName: 'IntroStep' },
      { id: 'calc',     label: 'זמן כניסה',        componentName: 'MoadCalcStep' },
      { id: 'days',     label: 'ימי המועד',         componentName: 'MoadDaysStep' },
      { id: 'zmanim',   label: 'זמן א׳ וזמן ב׳',   componentName: 'MoadZmanimPerDayStep' },
      { id: 'prayers',  label: 'תפילות מועד',       componentName: 'PrayerListStep', skippable: true, props: { mode: 'moad' } },
      { id: 'drashot',  label: 'דרשות המועד',       componentName: 'MoadDrashotStep', skippable: true },
      { id: 'leyning',  label: 'קריאת תורה',        componentName: 'LeyningSettingsStep', skippable: true },
      { id: 'template', label: 'תבנית תצוגה',      componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  TefilotWidget: {
    intro: {
      title: 'תפילות יום חול',
      emoji: '🤲',
      description:
        'מציג לוח זמני תפילות לימות החול. מחשב עבור היום הנוכחי בלבד — תפילות יחסיות לזמנים הלכתיים מחושבות מחדש בכל יום. תפילות חוזרות (כגון שחרית כל 30 דקות מ-6:30 עד 8:30) מרחיבות לרשימת שעות בודדות.',
      note: 'ניתן להגדיר ימי עקיפה — שחרית ביום ראשון בשעה אחרת, זמן מיוחד לראש חודש — מבלי ליצור רכיב נפרד.',
      whenToUse: 'הרכיב המרכזי לכל בית כנסת לצרכי תפילות יום-יום.',
    },
    steps: [
      { id: 'intro',             label: 'מבוא',              componentName: 'IntroStep' },
      { id: 'prayers',           label: 'הוספת תפילות',      componentName: 'PrayerListStep', props: { mode: 'tefilot' } },
      { id: 'timelineValidity',  label: 'תוקף תצוגה',        componentName: 'TimelineValidityStep', skippable: true },
      { id: 'template',          label: 'תבנית תצוגה',       componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  TextWidget: {
    intro: {
      title: 'תוכן חופשי',
      emoji: '📝',
      description:
        'תיבת טקסט להצגת תוכן קצר ונקודתי — פרשת שבוע, שעות פתיחה, ציטוט, מידע מוסדי. מיועד למשפטים ממוקדים ולא לרשימת מודעות.',
      note: 'לא מיועד למודעות! להודעות ועדכונים לקהל — השתמש ברכיב "לוח מודעות" הייעודי, שתומך בתאריכי תפוגה וגלילה אוטומטית. רכיב זה סטטי לחלוטין — מה שמקלידים נשאר עד לעריכה ידנית.',
      whenToUse: 'לתכנים קצרים שמתחלפים לאט — פרשת שבוע שבועית, שעות פתיחה, מידע מוסדי קבוע. לא לתקשורת שוטפת עם הקהל.',
    },
    steps: [
      { id: 'intro',    label: 'מבוא',         componentName: 'IntroStep' },
      { id: 'content',  label: 'תוכן',         componentName: 'TextContentStep' },
      { id: 'template', label: 'תבנית תצוגה', componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  DocumentWidget: {
    intro: {
      title: 'מסמך',
      emoji: '📄',
      description:
        'מציג מסמך PDF או Word על המסך ביחס המתאים למסמך. ניתן לבחור עמוד ספציפי או להפעיל גלילה אוטומטית בין עמודים.',
      note: 'מסמכי Word ניתנים לעריכה ישירות מהמערכת. למסמכים מורכבים (טבלאות מסובכות, עמודות) מומלץ להמיר ל-PDF לפני העלאה.',
      whenToUse: 'להצגת חוזרים, לוחות זמנים, מכתבים, או כל מסמך שרוצים לשתף עם הקהל.',
    },
    steps: [
      { id: 'intro',    label: 'מבוא',          componentName: 'IntroStep' },
      { id: 'document', label: 'העלאת מסמך',    componentName: 'DocumentStep' },
      // אין שלב תבנית — שמירה ישירה (כמו ImageWidget)
    ],
  },

  // ─────────────────────────────────────────────
  ImageWidget: {
    intro: {
      title: 'תמונה / לוגו',
      emoji: '🖼️',
      description:
        'מציג תמונה או לוגו — לוגו בית הכנסת, שלט שותפים, כרזת אירוע.',
      note: 'סטטי — לא מתחלף אוטומטית. לשינוי יש לערוך ידנית.',
      whenToUse: 'לזהות ויזואלית קבועה, או לפרסום עונתי שמחליפים ידנית.',
    },
    steps: [
      { id: 'intro',  label: 'מבוא',        componentName: 'IntroStep' },
      { id: 'image',  label: 'בחירת תמונה', componentName: 'ImageStep' },
      // אין שלב תבנית — שמירה ישירה
    ],
  },

  // ─────────────────────────────────────────────
  WeatherWidget: {
    intro: {
      title: 'מזג האוויר',
      emoji: '⛅',
      description:
        'תחזית מזג אוויר לפי מיקום בית הכנסת. נשלף ממקור חיצוני ומתרענן כל 30 דקות.',
      note: 'תלוי במיקום שהוגדר בהגדרות הארגון. אם המיקום לא הוגדר — יופיע שגיאה. אין צורך בהגדרות נוספות.',
      whenToUse: 'תוספת שימושית לציבור בעת יציאה מהתפילה — במיוחד בחורף ובקיץ.',
    },
    steps: [
      { id: 'intro',    label: 'מבוא',         componentName: 'IntroStep' },
      { id: 'template', label: 'תבנית תצוגה', componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  NoticeBoardWidget: {
    intro: {
      title: 'לוח מודעות',
      emoji: '📋',
      description:
        'לוח מודעות דיגיטלי לעדכונים לקהל. כשיש מודעות רבות — גולל אוטומטית בתנועה רציפה.',
      note: 'ניתן להגדיר לכל מודעה תאריך תפוגה — המודעה תיעלם אוטומטית כשהזמן יגיע.',
      whenToUse: 'לתקשורת שוטפת — שינויי זמנים זמניים, אירועים קרובים, הודעות דחופות.',
    },
    steps: [
      { id: 'intro',    label: 'מבוא',           componentName: 'IntroStep' },
      { id: 'notices',  label: 'הוספת מודעות',   componentName: 'GenericListStep', props: {
          fieldKey: 'notices',
          addLabel: 'הוסף מודעה',
          emptyLabel: 'אין מודעות עדיין',
          fields: [
            { key: 'content',    label: 'תוכן המודעה',          type: 'richtext',       required: true },
            { key: 'expiryDate', label: 'תפוגה (אופציונלי)',     type: 'datetime-local', required: false },
          ],
        }},
      { id: 'template', label: 'תבנית תצוגה',    componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  EmergencyWidget: {
    intro: {
      title: 'כוחות חירום',
      emoji: '🚑',
      description:
        'מציג רשימת מתנדבי חירום של הקהילה לפי קטגוריות: מגן דוד אדום, חובשים, חוליית חבלה.',
      note: 'סטטי לחלוטין — יש לעדכן ידנית כשמתנדב עוזב או מצטרף. מומלץ לעדכן מדי רבעון.',
      whenToUse: 'בכל בית כנסת עם נוכחות מתנדבי חירום.',
    },
    steps: [
      { id: 'intro',      label: 'מבוא',          componentName: 'IntroStep' },
      { id: 'volunteers', label: 'מתנדבים',       componentName: 'EmergencyStep' },
      { id: 'template',   label: 'תבנית תצוגה',   componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  ContactsWidget: {
    intro: {
      title: 'אנשי קשר',
      emoji: '📞',
      description:
        'ספר טלפונים של עובדי ופעילי הקהילה עם שעות זמינות.',
      note: 'סטטי — יש לעדכן ידנית בכל שינוי תפקיד או מספר.',
      whenToUse: 'לקהילות גדולות עם מספר תפקידים שהציבור צריך להגיע אליהם.',
    },
    steps: [
      { id: 'intro',    label: 'מבוא',            componentName: 'IntroStep' },
      { id: 'contacts', label: 'אנשי קשר',         componentName: 'GenericListStep', props: {
          fieldKey: 'contacts',
          addLabel: 'הוסף איש קשר',
          emptyLabel: 'אין אנשי קשר עדיין',
          fields: [
            { key: 'name',          label: 'שם',           type: 'text',   placeholder: 'שם מלא', required: true },
            { key: 'role',          label: 'תפקיד',        type: 'text',   placeholder: 'רב, גבאי, מנהל...' },
            { key: 'phone',         label: 'טלפון',        type: 'tel',    placeholder: '050-0000000' },
            { key: 'availableTime', label: 'שעות זמינות',  type: 'text',   placeholder: 'ימים א-ה, 9:00-17:00' },
          ],
        }},
      { id: 'template', label: 'תבנית תצוגה',     componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  SimchaWidget: {
    intro: {
      title: 'שמחות הקהילה',
      emoji: '🎊',
      description:
        'מציג שמחות ואירועי קהילה — חתונות, ברית מילה, בר/בת מצווה, אירוסין ועוד. כשיש שמחות רבות — גולל אוטומטית בתנועה חלקה.',
      note: 'ניתן להגדיר לכל שמחה תאריך תפוגה — תוסר אוטומטית לאחר האירוע.',
      whenToUse: 'ליצירת אווירה קהילתית ולשיתוף הציבור בשמחות בית הכנסת.',
    },
    steps: [
      { id: 'intro',   label: 'מבוא',            componentName: 'IntroStep' },
      { id: 'simchas', label: 'הוספת שמחות',     componentName: 'GenericListStep', props: {
          fieldKey: 'simchas',
          addLabel: 'הוסף שמחה',
          emptyLabel: 'אין שמחות עדיין',
          fields: [
            { key: 'name',           label: 'שם המשפחה',                   type: 'text', placeholder: 'כהן, לוי...', required: true },
            { key: 'celebrant',      label: 'שם הבן / בת / חתן / כלה',    type: 'text', placeholder: 'משה, רחל, דוד ושרה...' },
            { key: 'type',           label: 'סוג שמחה',          type: 'select',         options: [
                { value: 'wedding_boy',      label: 'חתונה בן' },
                { value: 'wedding_girl',     label: 'חתונה בת' },
                { value: 'bar_mitzvah',      label: 'בר מצווה' },
                { value: 'birth_boy',        label: 'הולדת בן' },
                { value: 'birth_girl',       label: 'הולדת בת' },
                { value: 'grandchild_boy',   label: 'הולדת נכד' },
                { value: 'grandchild_girl',  label: 'הולדת נכדה' },
                { value: 'engagement_boy',   label: 'אירוסין בן' },
                { value: 'engagement_girl',  label: 'אירוסין בת' },
                { value: 'other',            label: 'אחר' },
              ]},
            { key: 'customType',     label: 'פרט את סוג השמחה', type: 'text',           placeholder: 'שם השמחה...', showWhen: { field: 'type', value: 'other' } },
            { key: 'additionalText', label: 'טקסט נוסף',         type: 'text',           placeholder: 'פרטים נוספים...' },
            { key: 'expiryDate',     label: 'תאריך סיום הצגה (אופציונלי)', type: 'datetime-local', required: false },
          ],
        }},
      { id: 'template', label: 'תבנית תצוגה',    componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  NitzavSpotlightWidget: {
    intro: {
      title: 'נציב היום',
      emoji: '✨',
      description:
        'מדגיש אדם מיוחד — תורם, מנציח, אדם לכבוד — עם שם, הקדשה, וסוג הנציבות.',
      note: 'אין רוטציה אוטומטית — כל שינוי נציב מצריך עדכון ידני.',
      whenToUse: 'לכבד תורמים, להנציח נפטרים ביום היארצייט, לסמן אירועים מיוחדים.',
    },
    steps: [
      { id: 'intro',   label: 'מבוא',          componentName: 'IntroStep' },
      { id: 'nitzav',  label: 'פרטי הנציב',    componentName: 'NitzavStep' },
      { id: 'template', label: 'תבנית תצוגה', componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  ParnasWidget: {
    intro: {
      title: 'פרנס קהילה',
      emoji: '🕍',
      description:
        'ניהול רשימת פרנסים עם שיוך יומי, שבועי, או חודשי. מציג בזמן אמת מי הפרנסים הפעילים כעת לפי התאריך העברי — כולל מעבר יום הלכתי אחרי שקיעה.',
      note: 'ניתן לשייך מספר פרנסים לאותו זמן כל עוד הם בסוגים שונים (יומי + שבועי + חודשי). לכל פרנס ניתן להוסיף טקסט הקדשה ואייקון ספציפי.',
      whenToUse: 'לניהול נציבות מאור, קפה, אחזקת בית המדרש, אבות ובנים, ועוד.',
    },
    steps: [
      { id: 'intro',      label: 'מבוא',          componentName: 'IntroStep' },
      { id: 'parnasType', label: 'סוג פרנסות',    componentName: 'ParnasTypeStep' },
      { id: 'sponsors',   label: 'ניהול פרנסים',  componentName: 'SponsorsStep' },
      { id: 'template',   label: 'תבנית תצוגה',   componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  OmerWidget: {
    intro: {
      title: 'ספירת העומר',
      emoji: '🌾',
      description:
        'מציג את ספירת העומר — מליל ט"ז ניסן ועד ליל שבועות (49 ימים) — עם ברכה, ספירה מלאה בשבועות וימים, שמות הספירות הקבליות, ופסוקים. מתעדכן אוטומטית בשקיעה ועובר ליום הבא.',
      note: 'הרכיב פעיל רק בין ט"ז ניסן ל-ה׳ סיון. מחוץ לתקופה זו יוצג הודעה מתאימה.',
      whenToUse: 'להוסיף בתחילת חג הפסח. ניתן להשאיר כל השנה — מחוץ לעונה יוצג ריק בצורה מכובדת.',
    },
    steps: [
      { id: 'intro',    label: 'מבוא',          componentName: 'IntroStep' },
      { id: 'settings', label: 'הגדרות',         componentName: 'OmerSettingsStep' },
      { id: 'template', label: 'תבנית תצוגה',   componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  HalachicTimesWidget: {
    intro: {
      title: '\u05D6\u05DE\u05E0\u05D9 \u05D4\u05D9\u05D5\u05DD \u05D4\u05D4\u05DC\u05DB\u05EA\u05D9\u05D9\u05DD',
      emoji: '',
      description:
        '\u05DE\u05E6\u05D9\u05D2 \u05D0\u05EA \u05D4\u05D6\u05DE\u05E0\u05D9\u05DD \u05D4\u05D4\u05DC\u05DB\u05EA\u05D9\u05D9\u05DD \u05D1\u05DC\u05D1\u05D3 \u2014 \u05DE\u05E2\u05DC\u05D5\u05EA \u05D4\u05E9\u05D7\u05E8 \u05D5\u05E2\u05D3 \u05D7\u05E6\u05D5\u05EA \u05D4\u05DC\u05D9\u05DC\u05D4 \u2014 \u05D1\u05EA\u05E6\u05D5\u05D2\u05D4 \u05E0\u05E7\u05D9\u05D9\u05D4 \u05D5\u05E4\u05E9\u05D5\u05D8\u05D4, \u05DC\u05DC\u05D0 \u05EA\u05E4\u05D9\u05DC\u05D5\u05EA, \u05DC\u05DC\u05D0 \u05E9\u05D1\u05EA \u05D5\u05DC\u05DC\u05D0 \u05D6\u05DE\u05E0\u05D9\u05DD \u05DE\u05D5\u05EA\u05D0\u05DE\u05D9\u05DD.\n\n\u05E0\u05D9\u05EA\u05DF \u05DC\u05D1\u05D7\u05D5\u05E8 \u05D1\u05D3\u05D9\u05D5\u05E7 \u05D0\u05D9\u05DC\u05D5 \u05D6\u05DE\u05E0\u05D9\u05DD \u05D4\u05DC\u05DB\u05EA\u05D9\u05D9\u05DD \u05D9\u05D5\u05E6\u05D2\u05D5 \u05DE\u05E8\u05E9\u05D9\u05DE\u05D4 \u05DE\u05DC\u05D0\u05D4 \u05E9\u05DC \u05DB\u05DC \u05D4\u05D6\u05DE\u05E0\u05D9\u05DD \u05D4\u05D0\u05E4\u05E9\u05E8\u05D9\u05D9\u05DD.',
      note: '\u05E8\u05DB\u05D9\u05D1 \u05D6\u05DE\u05E0\u05D9\u05DD \u05D8\u05D4\u05D5\u05E8 \u2014 \u05D0\u05D9\u05DF \u05D1\u05D5 \u05EA\u05E4\u05D9\u05DC\u05D5\u05EA, \u05E9\u05D1\u05EA, \u05DE\u05D5\u05E2\u05D3 \u05D0\u05D5 \u05E2\u05E0\u05D9\u05D9\u05E0\u05D9 \u05D4\u05D9\u05D5\u05DD. \u05DC\u05E8\u05DB\u05D9\u05D1 \u05DE\u05D5\u05E8\u05DB\u05D1 \u05D9\u05D5\u05EA\u05E8 \u05E2\u05DD \u05DB\u05DC \u05D0\u05DC\u05D5 \u2014 \u05D4\u05E9\u05EA\u05DE\u05E9 \u05D1\u05E8\u05DB\u05D9\u05D1 "\u05E6\u05D9\u05E8 \u05D4\u05D6\u05DE\u05DF \u05D4\u05D9\u05D5\u05DE\u05D9".',
      whenToUse: '\u05DC\u05EA\u05E6\u05D5\u05D2\u05EA \u05D6\u05DE\u05E0\u05D9\u05DD \u05D4\u05DC\u05DB\u05EA\u05D9\u05D9\u05DD \u05E0\u05E7\u05D9\u05D9\u05D4 \u2014 \u05D1\u05DC\u05D9 \u05EA\u05E4\u05D9\u05DC\u05D5\u05EA \u05D5\u05D1\u05DC\u05D9 \u05DC\u05D5\u05D7 \u05D6\u05DE\u05E0\u05D9\u05DD \u05E7\u05D4\u05D9\u05DC\u05EA\u05D9.',
    },
    steps: [
      { id: 'intro',            label: '\u05DE\u05D1\u05D5\u05D0',          componentName: 'IntroStep' },
      { id: 'zmanimVisibility', label: '\u05D6\u05DE\u05E0\u05D9\u05DD \u05E2\u05DC \u05D4\u05E6\u05D9\u05E8', componentName: 'ZmanimVisibilityStep', skippable: true },
      { id: 'template',         label: '\u05EA\u05D1\u05E0\u05D9\u05EA \u05EA\u05E6\u05D5\u05D2\u05D4',   componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  LimudimWidget: {
    intro: {
      title: '\u05DC\u05D9\u05DE\u05D5\u05D3\u05D9\u05DD \u05D9\u05D5\u05DE\u05D9\u05D9\u05DD',
      emoji: '\uD83D\uDCDA',
      description:
        'מציג את כל סדרי הלימוד היומיים — דף יומי, משנה יומית, רמב"ם, נ"ך יומי, חפץ חיים, קיצור שולחן ערוך ועוד. כל סדר ניתן להפעלה/כיבוי בנפרד.',
      note: 'מתעדכן אוטומטית בשקיעה ובחצות. ניתן לבחור בדיוק אילו סדרי לימוד יוצגו.',
      whenToUse: 'לבתי מדרש וקהילות שרוצים להציג ללומדים את סדרי הלימוד היומיים.',
    },
    steps: [
      { id: 'intro',    label: 'מבוא',          componentName: 'IntroStep' },
      { id: 'settings', label: 'הגדרות',         componentName: 'LimudimSettingsStep' },
      { id: 'template', label: 'תבנית תצוגה',   componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  SeatingMapWidget: {
    intro: {
      title: 'מפת מושבים',
      emoji: '🪑',
      description:
        'מפה ויזואלית של מושבי בית הכנסת — מציגה את סידור המושבים, מי יושב היכן, ואילו מקומות פנויים.\n\n' +
        'המפה כוללת נקודות ציון: ארון הקודש, בימה וכניסה. ניתן להגדיר טורי מושבים בצורה גמישה — כל טור עם מספר שורות ומושבים שונה.',
      note: 'סטטי — יש לעדכן ידנית כשמשתנים שיוכי מקומות. ניתן ��ערוך גם ישירות מהלוח.',
      whenToUse: 'לבתי כנסת עם מקומות ישיבה קבועים.',
    },
    steps: [
      { id: 'intro',    label: 'מבוא',        componentName: 'IntroStep' },
      { id: 'map',      label: 'מפת מושבים',   componentName: 'SeatingMapStep' },
      { id: 'template', label: 'תבנית תצוגה', componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  ExpensesAnalyticsWidget: {
    intro: {
      title: 'ניתוח הוצאות',
      emoji: '📊',
      description:
        'גרף הוצאות לפי קטגוריות — עמודות או עוגה. שקיפות ויזואלית על חלוקת התקציב.',
      note: 'נתונים ידניים — אין חיבור אוטומטי לנהלת חשבונות. יש לעדכן בכל תקופה.',
      whenToUse: 'לדיווח שקוף לקהילה, ישיבות ועד, קמפיינים לגיוס תרומות.',
    },
    steps: [
      { id: 'intro',      label: 'מבוא',            componentName: 'IntroStep' },
      { id: 'categories', label: 'קטגוריות הוצאות', componentName: 'GenericListStep', props: {
          fieldKey: 'categories',
          addLabel: 'הוסף קטגוריה',
          emptyLabel: 'אין קטגוריות עדיין',
          fields: [
            { key: 'name',   label: 'שם הקטגוריה', type: 'text',   placeholder: 'תחזוקה, חשמל...', required: true },
            { key: 'amount', label: 'סכום (₪)',     type: 'number', placeholder: '5000' },
            { key: 'color',  label: 'צבע',          type: 'color' },
          ],
        }},
      { id: 'template',   label: 'תבנית תצוגה',     componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  ClockWidget: {
    intro: {
      title: 'שעון',
      emoji: '🕐',
      description:
        'שעון דיגיטלי או אנלוגי המציג את השעה המדויקת בזמן אמת — מתעדכן כל שנייה. האנלוגיים מגיעים בעיצובים עשירים ומכובדים המתאימים לאווירת בית כנסת: מסגרות זהב, פנים כחול עמוק, פנים שיש, ועיצוב נייבי מינימליסטי. הדיגיטליים מציגים שעות, דקות ושניות בפורמט 24 שעות, עם עיצוב גמיש לחלוטין.',
      note: 'רכיב תצוגה בלבד — אין להגדיר בו נתונים. צבע הרקע, צבע הטקסט והגופן של השעונים הדיגיטליים ניתנים לשינוי דרך הגדרות הרכיב.',
      whenToUse: 'בכל לוח — שעון מציג נוכחות ומקצועיות. מומלץ במיוחד לפינות הלוח כתוספת ויזואלית לרכיבי הזמן ההלכתיים.',
    },
    steps: [
      { id: 'intro',    label: 'מבוא',         componentName: 'IntroStep', props: { showTitleField: false } },
      { id: 'template', label: 'תבנית תצוגה', componentName: 'TemplateSplitStep' },
    ],
  },

  // ─────────────────────────────────────────────
  FinancialGaugeWidget: {
    intro: {
      title: 'מדד כלכלי',
      emoji: '💰',
      description:
        'מציג את יחס ההכנסות מול ההוצאות בצורת מד — מיידי, ברור, ויזואלי.',
      note: 'נתונים ידניים — אין חיבור אוטומטי. דורש עדכון תקופתי.',
      whenToUse: 'לתצוגה תמציתית של הבריאות הכלכלית בישיבות או לוחות ציבוריים.',
    },
    steps: [
      { id: 'intro',    label: 'מבוא',          componentName: 'IntroStep' },
      { id: 'income',   label: 'הכנסות',         componentName: 'FinancialItemsStep', props: { field: 'incomeData',   sectionLabel: 'הכנסות', color: '#1e3a5f' } },
      { id: 'expenses', label: 'הוצאות',         componentName: 'FinancialItemsStep', props: { field: 'expensesData', sectionLabel: 'הוצאות', color: '#e6c45e' } },
      { id: 'template', label: 'תבנית תצוגה',   componentName: 'TemplateSplitStep' },
    ],
  },
};

/**
 * getWizardConfig(componentKey)
 * מחזיר את הקונפיג של רכיב נתון, או null אם לא קיים.
 */
export const getWizardConfig = (componentKey) =>
  WIZARD_CONFIG[componentKey] || null;
